import React from 'react';
import { useTranslation } from 'react-i18next';
import Avatar from '../ProdFacAvatar';
import styled from 'styled-components';
import SelectedOffersEmptyRiskObjContainer from '../../selected-offers/SelectedOffersEmptyRiskObjContainer';
import SingleSelectedOffer from '../../selected-offers/single-selected-offers/SingleSelectedOffer';
import { Formulas } from '../../../../../../types/Quotes';
import {
  Grid,
  Text,
  Box,
  VStack,
  Flex,
  Switch,
  FormControl,
  FormLabel,
} from '@wegroup/design-system';
import { mq } from '../../../../../common/src/utils/styledComponentsUtils';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/redux/reducers/rootReducer';
import {
  setTerminationInfo,
  setTerminationLetterModal,
} from '../../../store/redux/slices/offersSlice';
import { hasTerminationInfo } from '../../../services/offersService';
import SwitchToggleAlert from './SwitchToggleAlert';
import { useDisclosure } from '@chakra-ui/hooks';

interface Props {
  selectedRiskObjects: string[];
  selectedOffers: Formulas;
}

const SelectedOffersContainerOverview: React.FC<
  React.PropsWithChildren<Props>
> = ({ selectedRiskObjects, selectedOffers }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const insuranceTerminationInfo = useSelector(
    (state: RootState) => state.offers.offersInfo.insuranceTerminationInfo,
  );

  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleOnSwitchToggle = (riskObject: string) => {
    if (hasTerminationInfo(insuranceTerminationInfo[riskObject])) {
      onOpen();
    } else {
      dispatch(setTerminationLetterModal(riskObject));
    }
  };

  const onRemove = (riskObject: string) => {
    dispatch(setTerminationInfo({ riskObject, data: 'none' }));
    onClose();
  };

  return (
    <Grid
      width="100%"
      mx="auto"
      gridGap={{ base: '0', laptop: '6' }}
      justifyContent="center"
      gridTemplateColumns={{
        base: '1fr',
        laptopL: '7.6rem minmax(auto, 100rem)',
      }}
      padding="6"
      maxW="104.8rem"
      data-test-id="ProdFac_SelectedOffersContainerOverview_Wrapper"
    >
      <StyledAvatar />
      <div>
        <Box mb="4" maxW="68rem">
          <Text
            textStyle="h1Headline"
            as="h1"
            mt="3"
            mb="4"
            display={{ base: 'none', laptop: 'block' }}
          >
            {t('These are your selected offers')}
          </Text>
          <Text
            textStyle="normalBody"
            color="gray.400"
            sx={{ span: { color: 'primary.500' } }}
          >
            {t(
              'View this overview of insurance packages prepared according to the perfect insurance to answer, to get the perfect insurance that meets your needs, without having to fulfill your obligations',
            )}
          </Text>
        </Box>
        <VStack spacing="12">
          {[...selectedRiskObjects].sort().map((riskObject: string) => (
            <Box w="100%" key={`riskObjectGroup_${riskObject}`}>
              <SwitchToggleAlert
                isOpen={isOpen}
                onClose={onClose}
                onRemove={() => onRemove(riskObject)}
              />
              <Flex
                direction={{ base: 'column', laptop: 'row' }}
                justifyContent="space-between"
                mb="6"
              >
                <Text textStyle="h4Headline">{t(riskObject)}</Text>
                {selectedOffers[riskObject]?.length > 0 &&
                  riskObject !== 'funeral' && (
                    <FormControl
                      display="flex"
                      justifyContent={{
                        base: 'flex-start',
                        laptop: 'flex-end',
                      }}
                    >
                      <FormLabel
                        htmlFor="terminationLetter"
                        mb="0"
                        alignSelf="center"
                      >
                        {t('With termination letter')}
                      </FormLabel>
                      <Switch
                        id="terminationLetter"
                        isChecked={hasTerminationInfo(
                          insuranceTerminationInfo[riskObject],
                        )}
                        onChange={() => handleOnSwitchToggle(riskObject)}
                      />
                    </FormControl>
                  )}
              </Flex>

              <VStack spacing="8" alignItems="stretch">
                {selectedOffers[riskObject]?.length > 0 ? (
                  selectedOffers[riskObject]?.map((quoteId) => (
                    <SingleSelectedOffer
                      key={quoteId}
                      quoteHashId={quoteId}
                      riskObject={riskObject}
                    />
                  ))
                ) : (
                  <SelectedOffersEmptyRiskObjContainer
                    riskObject={riskObject}
                  />
                )}
              </VStack>
            </Box>
          ))}
        </VStack>
      </div>
    </Grid>
  );
};

const StyledAvatar = styled(Avatar)`
  display: none;

  ${mq.laptopL} {
    display: block;
    width: 7.6rem;
    height: 7.6rem;
  }
`;

export default SelectedOffersContainerOverview;
